import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink } from '@angular/router';

import { resolveSignalValue } from '@lvflanders/bar-ng';

import { BaasActionButton } from '../baas-action-button/baas-action-button';
import { BaasActionButtonOptions, BaasActionButtonVariant } from '../baas-action-button/baas-action-button.types';
import { _BarInteractiveElementBehavior } from '../interactive-element/bar-interactive-element-behavior';
import { BaasMenuButtonItemOptions } from './baas-menu-button-item.types';

// NOTE based on bar-link, action-button.
@Component({
    selector: 'baas-menu-button',
    templateUrl: './baas-menu-button.html',
    imports: [RouterLink, MatButtonModule, MatMenuModule, BaasActionButton, MatIconModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Renders a button with a menu when clicked on. */
export class BaasMenuButton {
    readonly #clickableElementBehavior = inject(_BarInteractiveElementBehavior);

    /** Content in master button */
    readonly content = input<string>();
    /** Disabled or enabled */
    readonly disabled = input(false);
    /** Tooltip */
    readonly tooltip = input<string>();
    /** Icon class. */
    readonly iconClass = input<string>();
    readonly iconSrText = input<string>();
    /** Option variant of the button (mat-stroked-button,...) */
    readonly variant = input<BaasActionButtonVariant>('default');
    readonly items = input<BaasMenuButtonItemOptions[]>([]);

    readonly #iconSrText = computed(() => this.iconSrText() ?? this.tooltip());

    protected readonly actionButton = computed(() => {
        return {
            content: this.content(),
            disabled: this.disabled(),
            tooltip: this.tooltip(),
            variant: this.variant(),
            icon: this.iconClass()
                ? {
                      class: this.iconClass(),
                      iconOnly: !this.content(),
                      srText: this.#iconSrText()
                  }
                : undefined
        } as BaasActionButtonOptions;
    });

    protected readonly enrichedItems = computed(() => {
        return this.items().map((item) => {
            return {
                ...item,
                disabled: computed(() => resolveSignalValue(item.disabled)),
                hidden: computed(() => resolveSignalValue(item.hidden))
            };
        });
    });

    protected async buttonClick(event: any, item: BaasMenuButtonItemOptions) {
        await this.#clickableElementBehavior.buttonClick(event, item);
    }

    protected async linkClick(event: any, item: BaasMenuButtonItemOptions) {
        await this.#clickableElementBehavior.linkClick(event, item);
    }

    // invoked in case an action is defined and a routerLink
    protected async linkClickWithRouterCommands(event, item: BaasMenuButtonItemOptions) {
        await this.#clickableElementBehavior.linkClickWithRouterCommands(event, item);
    }
}
